import {type LexiconDoc} from '@atproto/lexicon'

/**
 * Override the SDK's app.bsky.notification.listNotifications lexicon.
 * The backend emits classroom notification reasons (classroom-request, classroom-approved,
 * classroom-rejected, classroom-post) and attaches reasonSubject as web route paths
 * (e.g. /classroom/<ownerHandle>/<roomHandle>).
 * In the default SDK lexicon, reasonSubject has format: 'at-uri' which causes XRPC response
 * validation errors for non-at-uri routes.
 */
export const listNotifications: LexiconDoc = {
  lexicon: 1,
  id: 'app.bsky.notification.listNotifications',
  defs: {
    main: {
      type: 'query',
      description:
        'Enumerate notifications for the requesting account. Requires auth.',
      parameters: {
        type: 'params',
        properties: {
          reasons: {
            description: 'Notification reasons to include in response.',
            type: 'array',
            items: {
              type: 'string',
              description:
                'A reason that matches the reason property of #notification.',
            },
          },
          limit: {
            type: 'integer',
            minimum: 1,
            maximum: 100,
            default: 50,
          },
          priority: {
            type: 'boolean',
          },
          cursor: {
            type: 'string',
          },
          seenAt: {
            type: 'string',
            format: 'datetime',
          },
        },
      },
      output: {
        encoding: 'application/json',
        schema: {
          type: 'object',
          required: ['notifications'],
          properties: {
            cursor: {
              type: 'string',
            },
            notifications: {
              type: 'array',
              items: {
                type: 'ref',
                ref: 'lex:app.bsky.notification.listNotifications#notification',
              },
            },
            priority: {
              type: 'boolean',
            },
            seenAt: {
              type: 'string',
              format: 'datetime',
            },
          },
        },
      },
    },
    notification: {
      type: 'object',
      required: [
        'uri',
        'cid',
        'author',
        'reason',
        'record',
        'isRead',
        'indexedAt',
      ],
      properties: {
        uri: {
          type: 'string',
          format: 'at-uri',
        },
        cid: {
          type: 'string',
          format: 'cid',
        },
        author: {
          type: 'ref',
          ref: 'lex:app.bsky.actor.defs#profileView',
        },
        reason: {
          type: 'string',
          description:
            'The reason why this notification was delivered - e.g. your post was liked, or you received a new follower.',
          knownValues: [
            'like',
            'repost',
            'follow',
            'mention',
            'reply',
            'quote',
            'starterpack-joined',
            'verified',
            'unverified',
            'like-via-repost',
            'repost-via-repost',
            'subscribed-post',
            'contact-match',
            'classroom-request',
            'classroom-approved',
            'classroom-rejected',
            'classroom-post',
          ],
        },
        reasonSubject: {
          type: 'string',
        },
        record: {
          type: 'unknown',
        },
        isRead: {
          type: 'boolean',
        },
        indexedAt: {
          type: 'string',
          format: 'datetime',
        },
        labels: {
          type: 'array',
          items: {
            type: 'ref',
            ref: 'lex:com.atproto.label.defs#label',
          },
        },
      },
    },
  },
}
